import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, CheckCheck } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUSES = ["Present", "Absent", "Half Day"];
const today = () => new Date().toISOString().slice(0, 10);

export default function BulkDailyAttendance() {
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = async (d) => {
    setLoading(true);
    try {
      const [wRes, aRes] = await Promise.all([
        api.get("/daily-workers"),
        api.get("/daily-attendance"),
      ]);
      const workers = wRes.data.filter((w) => String(w.status || "Active").toLowerCase() !== "inactive");
      const existing = {};
      aRes.data.filter((a) => a.work_date === d).forEach((a) => { existing[a.worker_id] = a; });
      setRows(workers.map((w) => ({
        worker_id: w.id, worker_name: w.name, job_role: w.job_role, department: w.department,
        status: existing[w.worker_id]?.status || existing[w.id]?.status || "Present",
        overtime_hours: existing[w.worker_id]?.overtime_hours ?? existing[w.id]?.overtime_hours ?? "",
      })));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  useEffect(() => { load(date); /* eslint-disable-next-line */ }, [date]);

  const setRow = (id, key, value) => setRows((rs) => rs.map((r) => (r.worker_id === id ? { ...r, [key]: value } : r)));
  const markAllPresent = () => setRows((rs) => rs.map((r) => ({ ...r, status: "Present" })));

  const save = async () => {
    setSaving(true);
    try {
      const entries = rows.map((r) => ({ worker_id: r.worker_id, worker_name: r.worker_name, status: r.status, overtime_hours: Number(r.overtime_hours) || 0 }));
      const { data } = await api.post("/daily-attendance/bulk", { work_date: date, entries });
      toast.success(data.message || "Attendance saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const summary = useMemo(() => {
    const s = { Present: 0, Absent: 0, "Half Day": 0 };
    rows.forEach((r) => { if (s[r.status] !== undefined) s[r.status] += 1; });
    return s;
  }, [rows]);

  const statusColor = (v) => v === "Present" ? "text-emerald-700" : v === "Absent" ? "text-red-700" : "text-amber-700";

  return (
    <div data-testid="bulk-daily-attendance">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <Button data-testid="bulk-back-btn" size="icon" variant="ghost" onClick={() => navigate("/daily-attendance")} className="h-9 w-9"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Bulk Daily Attendance</h1>
            <p className="text-sm text-slate-500 mt-0.5">Mark every active worker for one day at once · {rows.length} worker{rows.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input data-testid="bulk-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 bg-white" />
          <Button data-testid="bulk-mark-all-present" variant="outline" onClick={markAllPresent} className="rounded-sm gap-1.5"><CheckCheck className="w-4 h-4" /> <span className="hidden sm:inline">All Present</span></Button>
          <Button data-testid="bulk-save-btn" onClick={save} disabled={saving || !rows.length} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><Save className="w-4 h-4" /> {saving ? "Saving..." : "Save All"}</Button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {STATUSES.map((s) => (
          <div key={s} className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-sm" data-testid={`bulk-summary-${s.toLowerCase().replace(" ", "-")}`}>
            <span className={`font-bold tabular-nums ${statusColor(s)}`}>{summary[s]}</span> <span className="text-slate-500">{s}</span>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Worker</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Job Role</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-44">Status</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-36">Overtime (hrs)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-16 text-center text-slate-400" data-testid="bulk-loading">Loading workers...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-16 text-center text-slate-400" data-testid="bulk-empty">No active daily workers. Add workers first.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.worker_id} data-testid={`bulk-row-${r.worker_id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-2.5 font-medium text-slate-800">{r.worker_name}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.job_role || "-"}</td>
                  <td className="px-4 py-2.5">
                    <Select value={r.status} onValueChange={(v) => setRow(r.worker_id, "status", v)}>
                      <SelectTrigger data-testid={`bulk-status-${r.worker_id}`} className="bg-white h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-2.5">
                    <Input data-testid={`bulk-ot-${r.worker_id}`} type="number" min="0" value={r.overtime_hours} onChange={(e) => setRow(r.worker_id, "overtime_hours", e.target.value)} className="bg-white h-9" placeholder="0" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
